import { X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import type { ReactNode, RefObject } from 'react';
import { cn } from '../lib/cn';
import { useDirtyGuard } from './useDirtyGuard';
import { useReturnFocus } from './useReturnFocus';

const WIDTHS = { s: 'max-w-[400px]', m: 'max-w-[480px]', l: 'max-w-[560px]' } as const;

/**
 * Modal (DESIGN §4): 400–560 px, tło przyciemnione 32%, zaokrąglenie 14, przyciski na dole po prawej
 * (Ghost „Anuluj”, Primary akcja). Fokus w pułapce, Esc zamyka (Radix Dialog), po zamknięciu fokus wraca na element,
 * który dialog otworzył (`useReturnFocus`). Po wpisaniu czegokolwiek klik obok i Esc pytają „Zamknąć bez zapisu?”
 * zamiast zamykać (`useDirtyGuard`).
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'm',
  hideClose = false,
  closeLabel = 'Zamknij',
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: keyof typeof WIDTHS;
  hideClose?: boolean;
  /** etykieta krzyżyka dla czytnika ekranu — inna, gdy główny przycisk też zaczyna się od „Zamknij” */
  closeLabel?: string;
  className?: string;
}) {
  const onCloseAutoFocus = useReturnFocus(open);
  const guard = useDirtyGuard(open, onOpenChange);
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-overlay animate-fade-in" />
        <RadixDialog.Content
          onCloseAutoFocus={onCloseAutoFocus}
          {...guard.contentProps}
          className={cn(
            'fixed top-1/2 left-1/2 z-50 w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2',
            'max-h-[calc(100dvh-32px)] overflow-y-auto rounded-2xl bg-canvas p-6 shadow-modal animate-pop-in',
            'focus:outline-none',
            WIDTHS[width],
            className,
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="text-h4 text-fg">{title}</RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-2 text-body-s text-fg-secondary">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            {!hideClose && (
              <RadixDialog.Close
                aria-label={closeLabel}
                className="-mt-1 -mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
              >
                <X size={18} strokeWidth={1.75} aria-hidden />
              </RadixDialog.Close>
            )}
          </div>
          {children && <div className="mt-5">{children}</div>}
          {guard.confirm}
          {footer && <div className="mt-6 flex flex-wrap items-center justify-end gap-2">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Panel boczny (desktop: z prawej, 420 px) albo od dołu (mobile). Ten sam mechanizm co Dialog.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = 'right',
  className,
  initialFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'bottom' | 'left';
  className?: string;
  /** element, który dostaje fokus po otwarciu (domyślnie pierwszy fokusowalny — „Zamknij”) */
  initialFocusRef?: RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLElement | null>;
}) {
  const onCloseAutoFocus = useReturnFocus(open);
  const guard = useDirtyGuard(open, onOpenChange);
  const onOpenAutoFocus = initialFocusRef
    ? (event: Event) => {
        const el = initialFocusRef.current;
        if (!el) return;
        event.preventDefault();
        el.focus();
        // kursor na końcu wstawionego tekstu (np. pytanie z ⌘K)
        if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) {
          const end = el.value.length;
          el.setSelectionRange(end, end);
        }
      }
    : undefined;
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-overlay animate-fade-in" />
        <RadixDialog.Content
          onCloseAutoFocus={onCloseAutoFocus}
          {...guard.contentProps}
          {...(onOpenAutoFocus ? { onOpenAutoFocus } : {})}
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'fixed z-50 flex flex-col bg-canvas shadow-modal focus:outline-none',
            side === 'right' && 'inset-y-0 right-0 w-full max-w-[420px] animate-sheet-in',
            side === 'left' && 'inset-y-0 left-0 w-full max-w-[320px] animate-fade-in',
            side === 'bottom' && 'inset-x-0 bottom-0 max-h-[90dvh] rounded-t-2xl animate-sheet-up safe-bottom',
            className,
          )}
        >
          <div className="flex items-start gap-4 px-5 pt-5 pb-3">
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="text-h4 text-fg">{title}</RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-1 text-body-s text-fg-secondary">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close
              aria-label="Zamknij"
              // telefon: pole dotyku 44 px (lifting 17.10.2026), ikona w tym samym miejscu
              className="-mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent max-md:-my-1.5 max-md:-mr-3.5 max-md:size-11"
            >
              <X size={18} strokeWidth={1.75} aria-hidden />
            </RadixDialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
            {children}
            {guard.confirm}
          </div>
          {footer && <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
